import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import axios from "axios";
import { listHref } from "../shared/listUrlState";
import {
  Breadcrumbs,
  Button,
  Check,
  Download01,
  Edit01,
  Mail01,
  Metric,
  PageHeader,
  Phone01,
  TextInput,
  Upload01,
  Users01,
  XClose,
} from "@opendate-app/setlist";

import { segmentTitle } from "./segmentTitle";
import SegmentFansTable from "./SegmentFansTable";
import CSVImporter, { FAN_IMPORT_CHUNK_SIZE, FAN_IMPORT_RETRY_HINT } from "../../CSVImporter/CSVImporter";
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const rowStyle = { alignItems: "center", display: "flex", gap: 8 };

const metricsStyle = {
  display: "grid",
  gap: 16,
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  margin: "24px 0",
};

const SegmentDashboard = ({
  csrfToken,
  team,
  segment,
  targetedFansUrl,
}) => {
  const { title: displayName, family } = segmentTitle(segment);
  const [name, setName] = useState(segment.name);
  const [savedName, setSavedName] = useState(displayName);
  const [isEditingName, setIsEditingName] = useState(false);
  const [counts, setCounts] = useState({
    fans_count: segment.fans_count,
    email_count: segment.email_count,
    phone_number_count: segment.phone_number_count,
  });
  const isEditable = segment.type === "Segments::Manual" || segment.type === "Segments::DataDriven";

  const updateSegment = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/segments/${segment.id}`, { segment: { name } })
      .then(() => {
        setSavedName(name);
        setIsEditingName(false);
        toastSuccess("Segment updated successfully");
      })
      .catch(() => toastError("Could not update segment, please try again"));
  };

  const cancelEdit = () => {
    setName(savedName);
    setIsEditingName(false);
  };

  const exportCsv = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/csv_exports`, {
      type: "targeted_fans",
      exportable_id: segment.id,
    })
    .then(() => {
      toastSuccess("CSV export started, you will receive an email when it's ready to download");
    })
    .catch(() => toastError("Could not export CSV, something went wrong"));
  };

  const title = isEditingName ? (
    <form style={rowStyle} onSubmit={(e) => { e.preventDefault(); updateSegment(); }}>
      <div style={{ width: 400 }}>
        <TextInput
          autoFocus
          aria-label="Segment name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <Button type="submit" variant="tertiary" size="sm" icon={Check} aria-label="Save name" />
      <Button variant="tertiary" size="sm" icon={XClose} aria-label="Cancel" onClick={cancelEdit} />
    </form>
  ) : (
    <div style={rowStyle}>
      <span>{savedName}</span>
      {isEditable ? (
        <Button variant="tertiary" size="sm" icon={Edit01} aria-label="Rename segment" onClick={() => setIsEditingName(true)} />
      ) : null}
    </div>
  );

  const actions = (
    <div style={rowStyle}>
      {segment.type === "Segments::DataDriven" ? (
        <Button
          variant="secondary"
          icon={Edit01}
          onClick={() => { window.location.href = `/teams/${team.id}/segments/${segment.id}/edit`; }}
        >
          Edit
        </Button>
      ) : null}
      <Button variant="secondary" icon={Download01} onClick={exportCsv}>
        Export
      </Button>
      {segment.type === "Segments::Manual" ? (
        <CSVImporter
          csrfToken={csrfToken}
          team={team}
          importerKey={"ef329644-a20c-4084-bf4a-79d8ca44bca6"}
          type={"CSVImportRows::ManualSegmentFan"}
          chunkSize={FAN_IMPORT_CHUNK_SIZE}
          retryHint={FAN_IMPORT_RETRY_HINT}
          additionalParams={{ segment_id: segment.id }}
          onClose={() => window.location.reload() }
          renderAnchor={(openModal) => (
            <Button variant="primary" icon={Upload01} onClick={openModal}>
              Import
            </Button>
          )}
        />
      ) : null}
    </div>
  );

  return (
    <div>
      <ToastContainer icon={false} />
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: "Segments", href: listHref(`/teams/${team.id}/segments`) },
              { label: savedName },
            ]}
          />
        }
        title={title}
        supportingText={
          family || segment.calendar_event ? (
            <>
              {family}
              {family && segment.calendar_event ? " · " : null}
              {segment.calendar_event ? (
                <a href={`/teams/${team.id}/confirms/${segment.calendar_event.id}`}>
                  Go to Event Page →
                </a>
              ) : null}
            </>
          ) : null
        }
        actions={actions}
      />
      <div style={metricsStyle}>
        <Metric icon={Users01} label="People in segment" value={counts.fans_count.toLocaleString()} />
        <Metric icon={Mail01} label="Have emails" value={counts.email_count.toLocaleString()} />
        <Metric icon={Phone01} label="Have phone numbers" value={counts.phone_number_count.toLocaleString()} />
      </div>
      <SegmentFansTable dataUrl={targetedFansUrl} isManual={segment.type === "Segments::Manual"} onFanRemoved={setCounts} />
    </div>
  );
};

export default SegmentDashboard;
